/** Suite-certificate health, as a pure decision.
 *
 *  Split out of `SettingsPanel` deliberately. The interesting behaviour here is
 *  a five-way branch on two inputs and a clock, and every one of those branches
 *  is unreachable from a healthy rig: to see "expired" against a live daemon you
 *  would have to wait ninety days or break the operator's cert. A readout whose
 *  alarming states can never be exercised is exactly the kind of check that
 *  passes forever and then fails to fire on the one night it mattered, so the
 *  decision lives here where a smoke can drive all five in milliseconds and the
 *  component keeps only the rendering.
 *
 *  WHAT THE INPUTS MEAN, because the pair of them is the whole instrument.
 *  OPBridge reports `suite_cert_expires_at_unix_s` on `/health` at ALL times,
 *  but withholds `suite_hostname` once the cert enters its own renewal window
 *  (`opbridge-core/src/suite_cert.rs::health_fields`; their test
 *  `expiry_survives_the_renewal_window_but_hostname_does_not` pins the
 *  asymmetry). So "expiry present, hostname absent" IS the daemon telling us it
 *  no longer considers this name safe to hand out. `renewing` carries that
 *  signal rather than a re-derivation of their 30-day constant on this side -
 *  a second copy of a threshold is a second thing to drift.
 */

export type SuiteCertLevel = 'ok' | 'warn' | 'bad';

export interface SuiteCertStatus {
  text: string;
  level: SuiteCertLevel;
}

export interface SuiteCertInput {
  /** `/health.suite_cert_expires_at_unix_s`, as cached on the bridge row.
   *  `null` when this daemon has never issued one - a self-signed-only
   *  install, which is a supported state and not a fault. */
  expiresAtUnixS: number | null;
  /** Last poll reported an expiry but no hostname. See the module note. */
  renewing: boolean;
  /** Injected rather than read from the clock so the branches are testable
   *  and the row cannot show two different ages in one render. */
  nowMs: number;
}

/** Days at or below which an un-renewed cert stops being routine and becomes
 *  something the operator has to act on.
 *
 *  Picked from the FAILURE, not from taste. OPBridge only attempts renewal in a
 *  background task at daemon startup, and the fresh cert goes live on the
 *  restart AFTER that (`server.rs`, DEC-526) - so an always-on daemon, which is
 *  the normal shape of this product, can sit inside a 30-day window and never
 *  renew. Still un-renewed with a week left means roughly 23 days of chances
 *  went unused, which is genuinely wrong. Warning any earlier would light this
 *  row up for a month on a rig that is about to fix itself on the next restart,
 *  and a warning that is usually noise is one the operator learns to skip. */
export const SUITE_CERT_URGENT_DAYS = 7;

/** Whole days from `nowMs` to the expiry. Floored, so "0 days left" means
 *  today - the row never reads "1 day" while the cert is already dead. */
export function daysUntil(expiresAtUnixS: number, nowMs: number): number {
  return Math.floor((expiresAtUnixS * 1000 - nowMs) / 86_400_000);
}

export function describeSuiteCert(input: SuiteCertInput): SuiteCertStatus {
  const { expiresAtUnixS, renewing, nowMs } = input;

  if (expiresAtUnixS == null) {
    // Deliberately phrased as a fact, not an absence. This is what every
    // pre-v1.15.9x daemon looks like, and what a self-signed-only install
    // looks like forever; "unknown" would read as a fault and send the
    // operator hunting for a problem that is not there.
    return { text: 'Not issued - this bridge uses its self-signed address', level: 'ok' };
  }

  const days = daysUntil(expiresAtUnixS, nowMs);

  if (days <= 0) {
    // Says what actually happens, which is a fallback, not a dead rig: the
    // suite hostname stops being trusted and the connection returns to the LAN
    // address on its next reconcile. Overstating this would be its own bug -
    // an operator who reads "your rig is broken" mid-set will go looking for a
    // fault in the wrong place.
    return {
      text: 'Expired - the online path falls back to the LAN address on reconnect',
      level: 'bad',
    };
  }

  const plural = days === 1 ? '' : 's';

  if (!renewing) {
    return { text: `Valid - ${days} day${plural} left`, level: 'ok' };
  }

  if (days <= SUITE_CERT_URGENT_DAYS) {
    return {
      text: `Expires in ${days} day${plural} and has not renewed - `
        + 'restart OPBridge while it has internet',
      level: 'warn',
    };
  }

  return {
    text: `Renewing - completes next time OPBridge restarts online (${days} days left)`,
    level: 'ok',
  };
}
